import { z } from 'zod';

export const PLAN_IDS = ['free', 'plus', 'pro', 'max'] as const;
export type PlanId = (typeof PLAN_IDS)[number];

export const PAID_PLAN_IDS = ['plus', 'pro', 'max'] as const;
export type PaidPlanId = (typeof PAID_PLAN_IDS)[number];

export const PLAN_NAMES: Readonly<Record<PlanId, string>> = {
  free: 'Free',
  plus: 'Plus',
  pro: 'Pro',
  max: 'Max',
};

/** How often a paid plan can be billed. */
export const BILLING_INTERVALS = ['month', 'year'] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

/**
 * The status of a plan an administrator gave an account for free. Like a paid subscription it
 * gives the plan's credits, but nothing bills it, so there's nothing to pay for or cancel.
 */
export const COMPLIMENTARY_STATUS = 'complimentary';

export const UsageSchema = z
  .object({
    used: z.int().nonnegative().describe('Credits used today across all activities.'),
    limit: z.int().nonnegative().describe('Credits allowed per day on the current plan.'),
    remaining: z.int().nonnegative(),
    resetsAt: z.iso.datetime().describe('When the daily allowance resets (midnight UTC).'),
  })
  .meta({ id: 'Usage' });

export type Usage = z.infer<typeof UsageSchema>;

export const UsageBreakdownSchema = z
  .object({
    usage: UsageSchema,
    spent: z
      .object({
        rewrite: z.int().nonnegative(),
        tool: z.int().nonnegative(),
        dictation: z.int().nonnegative(),
        speech: z.int().nonnegative(),
        imageText: z.int().nonnegative().default(0),
      })
      .describe(
        'Credits used today on each activity: rewrites, AI tools, dictation, speech, and image text. They add up to usage.used at most; the rest went before they were counted this way, or, for visitors who aren’t signed in, on the same network.',
      ),
  })
  .meta({ id: 'UsageBreakdown' });

export type UsageBreakdown = z.infer<typeof UsageBreakdownSchema>;

export const PriceSchema = z
  .object({
    amount: z.int().nonnegative().describe('In the smallest currency unit, such as cents.'),
    currency: z.string(),
    interval: z.enum(['day', 'week', 'month', 'year']),
  })
  .meta({ id: 'Price' });

export type Price = z.infer<typeof PriceSchema>;

export const PlanSchema = z
  .object({
    id: z.enum(PLAN_IDS),
    name: z.string(),
    creditsPerDay: z.int().positive(),
    prices: z
      .object({ month: PriceSchema.nullable(), year: PriceSchema.nullable() })
      .describe(
        'The price billed monthly and yearly. Both are null for the free plan, and when prices can’t be loaded.',
      ),
  })
  .meta({ id: 'Plan' });

export type Plan = z.infer<typeof PlanSchema>;

export const PlansResponseSchema = z.object({
  plans: z.array(PlanSchema),
  planChangesEnabled: z.boolean().describe('Whether plan upgrades and changes are available.'),
});
export type PlansResponse = z.infer<typeof PlansResponseSchema>;

export const AccountSchema = z
  .object({
    user: z.object({ email: z.string() }).nullable().describe('Null when not signed in.'),
    plan: z.enum(PLAN_IDS),
    subscription: z
      .object({
        plan: z.enum(PAID_PLAN_IDS),
        interval: z
          .enum(BILLING_INTERVALS)
          .nullable()
          .describe('How often it’s billed; null when that isn’t known.'),
        status: z.string(),
        cancelAtPeriodEnd: z.boolean(),
        currentPeriodEnd: z.iso.datetime().nullable(),
      })
      .nullable(),
    usage: UsageSchema,
  })
  .meta({ id: 'Account' });

export type Account = z.infer<typeof AccountSchema>;

/**
 * Where to continue after signing in through an email link: checkout for a plan, billed monthly
 * or, with `:year`, yearly.
 */
export const SIGN_IN_NEXT = [
  'checkout:plus',
  'checkout:pro',
  'checkout:max',
  'checkout:plus:year',
  'checkout:pro:year',
  'checkout:max:year',
] as const;
export type SignInNext = (typeof SIGN_IN_NEXT)[number];

export interface CheckoutChoice {
  readonly plan: PaidPlanId;
  readonly interval: BillingInterval;
}

/** The continuation that resumes checkout for `plan` after signing in. */
export const checkoutNext = ({ plan, interval }: CheckoutChoice): SignInNext =>
  interval === 'year' ? `checkout:${plan}:year` : `checkout:${plan}`;

/** The checkout a continuation resumes, or null if it isn't one. */
export function parseCheckoutNext(next: string | null | undefined): CheckoutChoice | null {
  for (const plan of PAID_PLAN_IDS) {
    for (const interval of BILLING_INTERVALS) {
      if (next === checkoutNext({ plan, interval })) return { plan, interval };
    }
  }
  return null;
}

export const EmailLinkRequestSchema = z.object({
  email: z.email().max(254),
  next: z.enum(SIGN_IN_NEXT).optional(),
});

export type EmailLinkRequest = z.infer<typeof EmailLinkRequestSchema>;

/**
 * Characters in sign-in codes: digits and capital letters, without look-alikes (0 and O, 1, I,
 * and L), so a code read on one screen is easy to type on another.
 */
export const SIGN_IN_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';

/** Characters in the sign-in code, sent with the link for signing in on another device. */
export const SIGN_IN_CODE_LENGTH = 6;

const NOT_IN_CODES = new RegExp(`[^${SIGN_IN_CODE_ALPHABET}]`, 'g');

/** A code as typed or pasted, in its canonical form: capitals, without spaces or the dash. */
export const normalizeSignInCode = (input: string): string =>
  input.toUpperCase().replace(NOT_IN_CODES, '');

/** A sign-in code as shown, in two halves: K7M-4QX. */
export const formatSignInCode = (code: string): string =>
  code.length > SIGN_IN_CODE_LENGTH / 2
    ? `${code.slice(0, SIGN_IN_CODE_LENGTH / 2)}-${code.slice(SIGN_IN_CODE_LENGTH / 2)}`
    : code;

export const SignInCodeRequestSchema = z.object({
  email: z.email().max(254),
  code: z
    .string()
    .max(32)
    .describe(
      'The code from the sign-in email, such as K7M-4QX. Case, spaces, and the dash don’t matter.',
    ),
});

export type SignInCodeRequest = z.infer<typeof SignInCodeRequestSchema>;

export const CheckoutRequestSchema = z.object({
  plan: z.enum(PAID_PLAN_IDS),
  interval: z.enum(BILLING_INTERVALS).default('month').describe('How often to bill.'),
});
export type CheckoutRequest = z.infer<typeof CheckoutRequestSchema>;

export const RedirectResponseSchema = z.object({ url: z.string() });
export type RedirectResponse = z.infer<typeof RedirectResponseSchema>;

/** A subscriber's switch to another plan, or to the same plan billed at another interval. */
export const PlanChangeRequestSchema = z.object({
  plan: z.enum(PAID_PLAN_IDS),
  interval: z.enum(BILLING_INTERVALS),
});
export type PlanChangeRequest = z.infer<typeof PlanChangeRequestSchema>;

export const PlanChangePreviewSchema = z
  .object({
    action: z
      .enum(['charge', 'credit'])
      .describe('Charged now, or credited toward later bills, for the rest of this period.'),
    amount: z.int().nonnegative().describe('In the smallest currency unit, such as cents.'),
    currency: z.string(),
  })
  .meta({ id: 'PlanChangePreview' });
export type PlanChangePreview = z.infer<typeof PlanChangePreviewSchema>;

export const PaymentSchema = z
  .object({
    id: z.string().describe('Identifies the payment, as for its invoice.'),
    billedAt: z.iso.datetime(),
    amount: z.int().nonnegative().describe('What was paid, in the smallest currency unit.'),
    currency: z.string(),
  })
  .meta({ id: 'Payment' });
export type Payment = z.infer<typeof PaymentSchema>;

export const PaymentsResponseSchema = z.object({
  payments: z.array(PaymentSchema).describe('The latest payments, newest first.'),
});

/** What the web app needs to open the payment provider's checkout on its own pages. */
export const CheckoutSettingsSchema = z
  .object({
    paddle: z
      .object({
        environment: z.enum(['sandbox', 'production']),
        clientToken: z.string().describe('A client-side token: public, for Paddle.js only.'),
      })
      .nullable()
      .describe('Null when checkout isn’t run by Paddle, as with mock billing.'),
  })
  .meta({ id: 'CheckoutSettings' });
export type CheckoutSettings = z.infer<typeof CheckoutSettingsSchema>;
